<template>
  <sp-menu
    class="translation-toolbar-language-menu"
    location="bottom-right"
    :label="t('language_selector_label')"
    :open="menuOpen"
    @update-open="onMenuOpen"
  >
    <sp-button
      slot="activator"
      class="translation-toolbar-language-menu__activator"
      variant="outlined"
      color="surface"
      size="small"
      :aria-expanded="menuOpen"
      :accessible-name="selectorName"
      :data-testid="TestId.admin.translationToolbarLanguage"
    >
      <span class="translation-toolbar-language-menu__name">{{ selectedName }}</span>
      <sp-icon class="translation-toolbar-language-menu__caret" name="chevron-down" size="xx-small"></sp-icon>
    </sp-button>

    <ul class="translation-toolbar-language-menu__options" :aria-label="t('language_selector_label')">
      <li
        v-for="option in options"
        :key="option.locale"
        class="translation-toolbar-language-menu__option"
        :class="option.modifiers"
      >
        <sp-button
          variant="text"
          color="text"
          size="small"
          block
          :pressed="option.selected"
          :data-testid="TestId.admin.translationToolbarLanguageOption"
          :data-locale="option.locale"
          @click="selectLocale"
        >
          <sp-icon class="translation-toolbar-language-menu__check" name="check-alt-color" size="x-small"></sp-icon>
          <span class="translation-toolbar-language-menu__option-name">{{ option.name }}</span>
        </sp-button>
      </li>
    </ul>
  </sp-menu>
</template>

<script setup>
/**
 * The page-wide language selector: lists the source language and every language the record's
 * fields translate into, and selects one for every field through the translation page store.
 */
import { computed, ref } from "vue";

import { TestId } from "#/test-ids";
import { useTranslationPageStore } from "#ce/components/SpTranslatableField/store/translation-page";
import { t } from "./i18n";

const props = defineProps({
  defaultLocale: { type: String, required: true },
  defaultLanguageName: { type: String, required: true },
  languages: { type: Array, required: true },
});

const page = useTranslationPageStore();

const selectedLocale = computed(() => {
  const known = props.languages.some((language) => language.locale === page.locale);

  return known ? page.locale : props.defaultLocale;
});

function withSelection(option) {
  const selected = option.locale === selectedLocale.value;

  return { ...option, selected, modifiers: { "--is-selected": selected } };
}

const options = computed(() => {
  const source = {
    locale: props.defaultLocale,
    name: t("language_source", { language: props.defaultLanguageName }),
  };
  const translated = props.languages.map((language) => ({ locale: language.locale, name: language.name }));

  return [source, ...translated].map(withSelection);
});

const selectedName = computed(() => {
  if (selectedLocale.value === props.defaultLocale) {
    return props.defaultLanguageName;
  }

  return props.languages.find((language) => language.locale === selectedLocale.value)?.name ?? selectedLocale.value;
});

const selectorName = computed(() => `${t("language_selector_label")}: ${selectedName.value}`);

const menuOpen = ref(false);

function onMenuOpen({ detail }) {
  const [open] = detail;
  menuOpen.value = open;
}

function selectLocale({ currentTarget }) {
  page.select(currentTarget.dataset.locale);
  menuOpen.value = false;
}
</script>

<style lang="scss" scoped>
.translation-toolbar-language-menu {
  --menu-padding: var(--sp-comp-translation-toolbar-menu-padding, var(--sp-ref-scale-8));
  --menu-min-width: var(--sp-comp-translation-toolbar-menu-min-width, 14rem);

  // sp-menu is a full-width block by default; in the toolbar it takes its activator's width, and the
  // list grows to its longest row.
  --sp-comp-menu-min-width: var(--menu-min-width);
  --sp-comp-menu-width: max-content;

  flex: 0 0 auto;
  width: auto;

  // The activator is a pill: a rounded, hairline-bordered surface with the language and a caret.
  &__activator {
    --sp-comp-button-border-radius: var(--sp-ref-border-radius-full);
    --sp-comp-button-outlined-border-color: var(--sp-sys-color-outline-variant);
    --sp-comp-button-hover-outlined-border-color: var(--sp-sys-color-outline);
    --sp-comp-button-surface-outlined-text-color: var(--sp-sys-color-on-surface);
    --sp-comp-button-surface-hover-outlined-bg-color: var(--sp-sys-color-surface);
    --sp-comp-button-font-weight: var(--sp-ref-font-weight-normal);

    white-space: nowrap;
  }

  &__caret {
    margin-inline-start: var(--sp-ref-scale-4);
    vertical-align: middle;
  }

  &__options {
    box-sizing: border-box;
    min-width: var(--menu-min-width);
    margin: 0;
    padding: var(--menu-padding);
    list-style: none;
  }

  &__option sp-button {
    --sp-comp-button-justify-content: flex-start;
    --sp-comp-button-content-text-align: start;
  }

  &__check {
    margin-inline-end: var(--sp-ref-scale-6);
    visibility: hidden;
  }

  &__option.--is-selected &__check {
    visibility: visible;
  }

  &__option-name {
    flex: 1 1 auto;
    text-align: start;
  }
}
</style>
